import { Flame, Weight, Ruler } from "lucide-react";
import { useCatalog } from "./catalog";

export const SpecRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2 text-sm">
    <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-500" />
    <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 w-20 shrink-0">{label}</span>
    <span className="text-zinc-200">{value}</span>
  </div>
);

const Chips = ({ items, tone = "zinc" }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((x) => <span key={x} className={`rounded border px-2 py-0.5 text-[11px] ${tone === "green" ? "border-emerald-500/30 text-emerald-300" : tone === "red" ? "border-red-500/30 text-red-300" : "border-zinc-700 text-zinc-300"}`}>{x}</span>)}
  </div>
);

export const MaterialCard = ({ m }) => (
  <div data-testid={`material-card-${m.id}`} className="rounded-lg border border-zinc-800 bg-[#121215] p-6">
    <div className="mb-4 flex items-center gap-3">
      <span className="h-8 w-1.5 rounded-full" style={{ background: m.color }} />
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{m.category}</div>
        <h3 className="font-[Manrope] text-lg font-semibold">{m.name}</h3>
      </div>
    </div>
    <p className="mb-5 text-sm text-zinc-400">{m.description}</p>
    <div className="mb-5 space-y-2">
      <SpecRow icon={Ruler} label="Thickness" value={`${m.thickness_mm.join(" / ")} mm`} />
      <SpecRow icon={Weight} label="Weight" value={`${m.weight_kg_m2} kg/m²`} />
      <SpecRow icon={Flame} label="Fire" value={m.fire_class} />
      <SpecRow icon={Ruler} label="Max panel" value={`${m.max_panel_mm} mm`} />
    </div>
    <div className="space-y-3 text-sm">
      <div><div className="mb-1 font-mono text-[11px] uppercase text-zinc-500">Subframe</div><p className="text-zinc-300">{m.subframe}</p></div>
      <div><div className="mb-1 font-mono text-[11px] uppercase text-zinc-500">Finishes</div><Chips items={m.finishes} /></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div><div className="mb-1 font-mono text-[11px] uppercase text-zinc-500">Pros</div><Chips items={m.pros} tone="green" /></div>
        <div><div className="mb-1 font-mono text-[11px] uppercase text-zinc-500">Watch-outs</div><Chips items={m.cons} tone="red" /></div>
      </div>
      <div><div className="mb-1 font-mono text-[11px] uppercase text-zinc-500">Reference brands</div><p className="text-zinc-400">{m.brands.join(" · ")}</p></div>
      <div><div className="mb-1 font-mono text-[11px] uppercase text-zinc-500">Standards</div><p className="font-mono text-xs text-zinc-400">{m.standards.join(" · ")}</p></div>
    </div>
  </div>
);

export default function MaterialCatalog() {
  const catalog = useCatalog();
  return (
    <div>
      <div className="mb-10">
        <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Library</div>
        <h1 className="mt-1 font-[Manrope] text-3xl font-extrabold tracking-tight md:text-4xl">Material Catalog</h1>
        <p className="mt-2 max-w-2xl text-sm text-zinc-400">Typical industry specifications for each facade material. Always check final values against the manufacturer's data sheets.</p>
      </div>
      <div className="grid gap-6 lg:grid-cols-2 2xl:grid-cols-3" data-testid="material-catalog-grid">
        {catalog?.materials.map((m) => <MaterialCard key={m.id} m={m} />)}
      </div>
    </div>
  );
}
